import React from "react";

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  isChunkError: boolean;
}

/** Returns true for "Failed to fetch dynamically imported module" and similar
 *  chunk-load failures that happen after a new deployment invalidates old hashes. */
function detectChunkError(error: Error): boolean {
  const msg = error?.message ?? "";
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("Loading chunk") ||
    msg.includes("Loading CSS chunk") ||
    // Vite / webpack generic dynamic import failures
    /import.*chunk/i.test(msg)
  );
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, isChunkError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, isChunkError: detectChunkError(error) };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("ErrorBoundary caught:", error, info);

    // Chunk-Load-Fehler nach neuem Deploy → einmalig automatisch hart neu laden.
    // "alta_chunk_reload" verhindert eine Reload-Schleife falls das Laden
    // auch nach dem Refresh fehlschlägt.
    if (detectChunkError(error)) {
      const key = "alta_chunk_reload";
      const last = Number(sessionStorage.getItem(key) ?? "0");
      const now = Date.now();
      if (now - last > 10_000) {
        sessionStorage.setItem(key, String(now));
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      const { isChunkError, error } = this.state;

      return (
        <div
          style={{
            padding: "3rem 2rem",
            textAlign: "center",
            color: "var(--color-text-muted, #6c757d)",
          }}
        >
          <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>⚠️</div>
          <h2
            style={{
              color: "var(--color-danger, #d82e91)",
              marginBottom: "0.5rem",
            }}
          >
            Etwas ist schiefgelaufen
          </h2>
          <p style={{ marginBottom: "1.5rem", fontSize: "0.9rem" }}>
            {isChunkError
              ? "Die App wurde aktualisiert. Bitte Seite neu laden."
              : (error?.message ?? "Unbekannter Fehler")}
          </p>
          <button
            onClick={() => {
              if (isChunkError) {
                window.location.reload();
              } else {
                this.setState({ hasError: false, error: undefined, isChunkError: false });
              }
            }}
            style={{
              background: "var(--color-primary, #143c89)",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              padding: "0.5rem 1.2rem",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            {isChunkError ? "Jetzt neu laden" : "Erneut versuchen"}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
